import { useSyncExternalStore } from "react";

export type CartItem = {
  id: string;
  name: string;
  price: number; // unit price in USD
  qty: number;
  meta?: string; // e.g. "12 mo", "Pro plan"
  href?: string; // link back to product page
};

const STORAGE_KEY = "coderlity_cart_v1";

type Listener = () => void;
const listeners = new Set<Listener>();
let items: CartItem[] = load();

function load(): CartItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isItem) : [];
  } catch {
    return [];
  }
}

function isItem(x: unknown): x is CartItem {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return typeof o.id === "string" && typeof o.name === "string" &&
    typeof o.price === "number" && typeof o.qty === "number";
}

function persist() {
  if (typeof window === "undefined") return;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* ignore */ }
}

function emit() {
  persist();
  listeners.forEach((l) => l());
}

// Cross-tab sync
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== STORAGE_KEY) return;
    items = load();
    listeners.forEach((l) => l());
  });
}

export const cartStore = {
  getSnapshot: () => items,
  subscribe(l: Listener) {
    listeners.add(l);
    return () => { listeners.delete(l); };
  },
  add(item: Omit<CartItem, "qty"> & { qty?: number }) {
    const qty = item.qty ?? 1;
    const existing = items.find((i) => i.id === item.id);
    if (existing) {
      items = items.map((i) => i.id === item.id ? { ...i, qty: i.qty + qty } : i);
    } else {
      items = [...items, { ...item, qty }];
    }
    emit();
  },
  setQty(id: string, qty: number) {
    if (qty <= 0) return cartStore.remove(id);
    items = items.map((i) => i.id === id ? { ...i, qty } : i);
    emit();
  },
  remove(id: string) {
    items = items.filter((i) => i.id !== id);
    emit();
  },
  clear() {
    items = [];
    emit();
  },
};

const EMPTY: CartItem[] = [];
function getServerSnapshot(): CartItem[] { return EMPTY; }

export function useCart() {
  const items = useSyncExternalStore(cartStore.subscribe, cartStore.getSnapshot, getServerSnapshot);
  const count = items.reduce((s, i) => s + i.qty, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  return { items, count, subtotal };
}
